import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { toast } from '@crowdex/ui/components/toaster';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { listAppArtifacts } from './artifacts-client';
import { artifactOfFile } from './use-artifact-index';

/** An artifact as a view or the agent names it: its app (or none), its space and path. */
export type ArtifactTarget = {
  plugin?: string | undefined;
  space: string;
  path: string;
  /** The conversation whose workspace it is in. */
  conversation?: string | undefined;
};

/** The artifact a target names, as the index lists it (a file of a directory type included). */
export async function findArtifactTarget(
  target: ArtifactTarget,
): Promise<ArtifactListingEntry | undefined> {
  const path = target.path.replace(/^\.?\//, '');
  const listed = await listAppArtifacts(target.conversation).catch(() => []);
  return target.space === 'workspace'
    ? artifactOfFile(
        listed.filter(
          (artifact) => target.plugin === undefined || artifact.plugin === target.plugin,
        ),
        path,
      )
    : listed.find(
        (artifact) =>
          artifact.plugin === target.plugin &&
          artifact.space === target.space &&
          artifact.path === path,
      );
}

/**
 * Docks the artifact a view's `view.open({ artifact })` (or the agent's `show`) names in the
 * conversation on screen: its tab in the side panel. One that is no artifact there is said so.
 */
export async function dockArtifactTarget(
  store: DesktopShellStore,
  target: ArtifactTarget,
): Promise<boolean> {
  const found = await findArtifactTarget(target);
  if (found === undefined) {
    toast.error('Artifact', `${target.path} is not an artifact here.`);
    return false;
  }
  dockArtifact(store, found.id);
  return true;
}
